// The phase 2 half of the API client: clients, resources, jobs and bookings.
//
// Split from api.ts purely for size — same `request`, same error handling.
import type { Attachment } from './documentsApi';
import type { JobMoneyBlock } from './jobMoneyApi';
import { ApiError, request } from './api';
import type { ExpiryStatus } from './documentsApi';

/**
 * The six kinds (migration 011). `plant` and `vehicle` are the ASSETS — the
 * ones that carry an identifier, a registration and, from batch 2, meters and
 * a service history. `supplier` is who does maintenance and is never booked;
 * the server refuses it as a booking resource, and the pickers never offer it.
 */
export type ResourceKind =
  | 'person'
  | 'plant'
  | 'vehicle'
  | 'subcontractor'
  | 'supplier'
  | 'other';

/** The two kinds the fleet fields apply to. Mirrors src/lib/resourceTypes.ts. */
export const ASSET_KINDS: ResourceKind[] = ['plant', 'vehicle'];

/** Everything a booking may name. A supplier is deliberately absent. */
export const BOOKABLE_KINDS: ResourceKind[] = [
  'person',
  'plant',
  'vehicle',
  'subcontractor',
  'other',
];

/**
 * Shared with document expiry, so one badge treatment covers both — and now
 * shared literally. It was declared here a second time, character for
 * character, and StatusBadge takes either: two names for one thing, with
 * nothing to notice if a fifth state were ever added to only one of them.
 */
export type { ExpiryStatus };
export type ResourceStatus = 'available' | 'unavailable' | 'archived';
export type JobStatus =
  | 'pending'
  | 'scheduled'
  | 'in_progress'
  /** Round 45: parked. Manual, sticky and still open. */
  | 'on_hold'
  | 'completed'
  /** Round 34: one step past completed — the work has been billed. */
  | 'invoiced'
  | 'cancelled';

export interface Client {
  id: string;
  name: string;
  contactPerson: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  accountsName: string | null;
  accountsPhone: string | null;
  accountsEmail: string | null;
  abn: string | null;
  acn: string | null;
  archived: boolean;
  isSampleData: boolean;
}

/** One of a client's representatives — who the crew rings about the work. */
export interface ClientRep {
  id: string;
  clientId: string;
  name: string;
  phone: string | null;
  email: string | null;
  notes: string | null;
  archived: boolean;
}

/** One internal note on a job. Same shape and same rules as a client's. */
export interface JobNote {
  id: string;
  jobId: string;
  body: string;
  createdByName: string;
  createdAt: string;
}

/** One note against a client. Append-only: there is no edit or delete. */
export interface ClientNote {
  id: string;
  clientId: string;
  body: string;
  createdByName: string;
  createdAt: string;
}

export interface Resource {
  id: string;
  name: string;
  kind: ResourceKind;
  category: string | null;
  status: ResourceStatus;
  notes: string | null;
  linkedUserId: string | null;
  isSampleData: boolean;

  // --- The fleet fields (migration 011) ---
  //
  // Always present, null where the kind cannot hold them, so nothing has to
  // guess which shape it received.
  identifier: string | null;
  /** Name plus identifier when there is one, name alone when there is not. */
  label: string;
  registrationNumber: string | null;
  registrationExpiry: string | null;
  registrationExpiryStatus: ExpiryStatus;
  soldDate: string | null;
  soldTo: string | null;
  nextServiceReading: number | null;
  nextServiceDate: string | null;
  serviceIntervalValue: number | null;
  serviceIntervalUnit: 'km' | 'hours' | 'months' | null;

  // --- Round 28: the machine type ---
  machineTypeId: string | null;
  /** Resolved where the server joined for it; null otherwise. */
  machineTypeName: string | null;

  // --- Round 37: a person's own dates ---
  //
  // Null on every other kind, and on any person whose company has not
  // recorded them — the ordinary case, and not a gap to be filled.
  dateOfBirth: string | null;
  employmentStartDate: string | null;
}

export interface Unavailability {
  id: string;
  resourceId: string;
  startDate: string;
  endDate: string;
  reason: string;
}

export type NonWorkingDayKind = 'public_holiday' | 'rdo' | 'shutdown' | 'other';

/** A named company-wide non-working range (migration 021, round 22). */
export interface NonWorkingDay {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  kind: NonWorkingDayKind;
  notes: string | null;
}

export interface Job {
  id: string;
  number: string;
  title: string;
  clientId: string | null;
  siteAddress: string | null;
  description: string | null;
  /** The client's PO for this work, when they issued one (migration 032). */
  purchaseOrderNumber: string | null;
  /**
   * The dates the client was told (round 34). Informational only — the
   * bookings are still the schedule, and nothing derives from these.
   */
  startDate: string | null;
  endDate: string | null;
  jobManagerId: string | null;
  clientRepId: string | null;
  status: JobStatus;
  cancelReason: string | null;
  createdAt: string;
  isSampleData: boolean;
}

/** A list row carries only what the list draws — the detail route serves the
    description, the PO, the dates, the manager and the rep. */
export interface JobListItem
  extends Omit<
    Job,
    'description' | 'purchaseOrderNumber' | 'startDate' | 'endDate' | 'jobManagerId' | 'clientRepId'
  > {
  clientName: string | null;
  nextBookedDate: string | null;
  bookingCount: number;
  conflictCount: number;
}

/** 'job' or a machine booked in for maintenance (migration 013). */
export type BookingKind = 'job' | 'maintenance';

export interface Booking {
  id: string;
  kind: BookingKind;
  /** Null on maintenance — `supplierId` is set instead. */
  jobId: string | null;
  /** Null on a job booking. */
  supplierId: string | null;
  resourceId: string;
  startDate: string;
  endDate: string;
  /** Always null on maintenance: a machine at the mechanic is away all day. */
  startTime: string | null;
  endTime: string | null;
  note: string | null;
  inConflict: boolean;
}

/**
 * Enough of a booking to SAY WHAT IT IS — the job it is on, or the supplier
 * a machine went to (round 37). All three are null on the other species, so
 * `bookingLabel` can read it without a second query.
 */
export interface LabelledBooking extends Booking {
  jobNumber: string | null;
  jobTitle: string | null;
  supplierName: string | null;
}

export interface CalendarBooking extends Booking {
  // All four are null on a maintenance booking. Render `supplierName` instead
  // of reaching for a job that is not there.
  jobNumber: string | null;
  jobTitle: string | null;
  jobStatus: JobStatus | null;
  clientName: string | null;
  supplierName: string | null;
  resourceName: string;
  resourceKind: ResourceKind;
  resourceCategory: string | null;
  /** Both null on every kind but `plant` and `vehicle` — see resourceTypes.ts
      on the server, which is the one place that rule lives. The agenda puts
      them on its Plant line; nothing else does, deliberately. */
  resourceIdentifier: string | null;
  resourceRegistration: string | null;
}

/**
 * A booking the server has just flagged as needing re-planning.
 *
 * The three extras were typed as plain strings, which stopped being true at
 * migration 013: a maintenance booking has no job, so a notice built by
 * concatenating them read "now need re-planning: null" to whoever had just
 * marked a machine unavailable. `bookingLabel` is what turns these into
 * something a person can read.
 */
export interface AffectedBooking extends Booking {
  jobNumber: string | null;
  jobTitle: string | null;
  supplierName: string | null;
}

/**
 * A booking the leave being added would land on — the 409 `bookings_in_way`
 * payload (round 36). `rewritesPast` is the server's advisory answer to
 * "would releasing this touch days already worked", so the dialog can show
 * the past-days tick up front; the release itself re-derives it under the
 * lock.
 */
export interface BookingInWay extends AffectedBooking {
  rewritesPast: boolean;
}

export type ReleaseOverlapPlan =
  | { action: 'none' }
  | { action: 'delete'; rewritesPast: boolean }
  | { action: 'trim'; startDate: string; endDate: string; rewritesPast: boolean }
  | {
      action: 'split';
      startDate: string;
      endDate: string;
      tailStartDate: string;
      tailEndDate: string;
      rewritesPast: boolean;
    };

/** What the conflict panel shows when a booking is refused. */
export type Conflict =
  | {
      type: 'booking';
      kind: BookingKind;
      bookingId: string;
      /** Null on maintenance; `supplierName` says who has the machine. */
      jobId: string | null;
      jobNumber: string | null;
      jobTitle: string | null;
      supplierName: string | null;
      startDate: string;
      endDate: string;
      startTime: string | null;
      endTime: string | null;
      createdByName: string | null;
      /** The resource both bookings want — the panel groups per resource. */
      resourceId: string;
      /** What Move & Schedule would do to this booking (round 22). */
      ifReleased: ReleaseOverlapPlan;
      /** Releasing whole days would give up hours outside the new window. */
      releasingLosesHours: boolean;
    }
  | {
      type: 'unavailability';
      unavailabilityId: string;
      reason: string;
      startDate: string;
      endDate: string;
      createdByName: string | null;
    }
  | { type: 'resource_status'; status: ResourceStatus; resourceName: string };

/**
 * Pulls the conflict panel out of a refused booking.
 *
 * There is no override anywhere in this product — the board never lies — so
 * the only useful thing to do with a 409 is show the manager what is in the
 * way and let them decide.
 */
export function conflictsFrom(error: unknown): Conflict[] | null {
  if (!(error instanceof ApiError) || error.code !== 'booking_conflict') return null;
  const details = error.details as { conflicts?: Conflict[] } | undefined;
  return details?.conflicts ?? null;
}

export interface BookingInput {
  jobId: string;
  resourceId: string;
  startDate: string;
  endDate: string;
  startTime?: string | null;
  endTime?: string | null;
  note?: string | null;
}

/**
 * The Move & Schedule fields (round 22), accepted by every save path.
 * `dryRun` checks under the real locks and writes nothing; `release` frees
 * the booked resource from the named clashes for the new booking's days;
 * `allowPastRewrite` is the explicit ask when a release touches worked days.
 */
export interface SaveOptions {
  dryRun?: boolean;
  release?: string[];
  allowPastRewrite?: boolean;
}

function query(params: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, value);
  }
  const text = search.toString();
  return text === '' ? '' : `?${text}`;
}

export interface MyAssignment {
  bookingId: string;
  startDate: string;
  endDate: string;
  startTime: string | null;
  endTime: string | null;
  note: string | null;
  job: {
    id: string;
    number: string;
    title: string;
    status: JobStatus;
    siteAddress: string | null;
    description: string | null;
    clientName: string | null;
  };
}

export const scheduling = {
  // --- Clients ---
  /**
   * `archived: true` is everything; `'only'` is the archived list on its own
   * (round 45, the Clients page's Archived tab).
   */
  listClients: (options: { search?: string; archived?: boolean | 'only' } = {}) =>
    request<{ clients: Client[] }>(
      `/clients${query({
        search: options.search,
        archived: options.archived === 'only' ? 'only' : options.archived ? 'true' : undefined,
      })}`
    ),
  importClients: (rows: Record<string, string>[]) =>
    request<{ imported: number }>('/clients/import', { method: 'POST', body: { rows } }),
  getClient: (id: string) =>
    request<{ client: Client; jobCount: number; reps: ClientRep[]; notes: ClientNote[] }>(
      `/clients/${id}`
    ),
  createClient: (body: Record<string, unknown>) =>
    request<{ client: Client }>('/clients', { method: 'POST', body }),
  updateClient: (id: string, body: Record<string, unknown>) =>
    request<{ client: Client }>(`/clients/${id}`, { method: 'PATCH', body }),
  archiveClient: (id: string, archived: boolean) =>
    request<{ client: Client }>(`/clients/${id}/archive`, { method: 'POST', body: { archived } }),

  // --- A client's representatives and notes (round 34) ---
  listClientReps: (clientId: string) =>
    request<{ reps: ClientRep[] }>(`/clients/${clientId}/reps`),
  createClientRep: (clientId: string, body: Record<string, unknown>) =>
    request<{ rep: ClientRep }>(`/clients/${clientId}/reps`, { method: 'POST', body }),
  updateClientRep: (clientId: string, repId: string, body: Record<string, unknown>) =>
    request<{ rep: ClientRep }>(`/clients/${clientId}/reps/${repId}`, { method: 'PATCH', body }),
  removeClientRep: (clientId: string, repId: string) =>
    request<void>(`/clients/${clientId}/reps/${repId}`, { method: 'DELETE' }),
  addClientNote: (clientId: string, body: string) =>
    request<{ note: ClientNote }>(`/clients/${clientId}/notes`, {
      method: 'POST',
      body: { body },
    }),

  // --- Resources ---
  listResources: (
    options: {
      kind?: string;
      status?: string;
      category?: string;
      /**
       * People with a competency or induction lapsing inside the company's
       * lead time, or already lapsed. A server-side FILTER on purpose — no
       * expiry travels back on the row. The list answers who; the person's own
       * page answers what (round 27).
       */
      needsAttention?: boolean;
      /**
       * Archived resources as well. The schedule needs them: an archived
       * machine that still has bookings keeps a row on the board, and the
       * editors for those bookings have to be able to name the machine.
       */
      includeArchived?: boolean;
    } = {}
  ) =>
    request<{ resources: Resource[] }>(
      `/resources${query({
        ...options,
        includeArchived: undefined,
        archived: options.includeArchived ? 'true' : undefined,
        needsAttention: options.needsAttention ? 'true' : undefined,
      })}`
    ),
  resourceCategories: () => request<{ categories: string[] }>('/resources/categories'),
  createResource: (body: Record<string, unknown>) =>
    request<{ resource: Resource }>('/resources', { method: 'POST', body }),
  /**
   * Bulk import. All or nothing on the server — a row that fails takes the
   * whole import with it, because a half-finished import is worse than a
   * refused one: you cannot tell which half landed.
   */
  importResources: (
    rows: {
      name: string;
      kind?: string;
      category?: string;
      notes?: string;
      identifier?: string;
      registrationNumber?: string;
      registrationExpiry?: string;
    }[]
  ) =>
    request<{ imported: number; resources: Resource[] }>('/resources/import', {
      method: 'POST',
      body: { rows },
    }),
  getResource: (id: string) =>
    request<{
      resource: Resource;
      unavailability: Unavailability[];
      bookings: LabelledBooking[];
    }>(`/resources/${id}`),
  updateResource: (id: string, body: Record<string, unknown>) =>
    request<{
      resource: Resource;
      affectedBookings: AffectedBooking[];
    }>(`/resources/${id}`, { method: 'PATCH', body }),
  addUnavailability: (
    resourceId: string,
    body: {
      startDate: string;
      endDate: string;
      reason: string;
      // Confirm flags (round 36): absent until the 409 dialog has asked;
      // true releases the leave days, false keeps the bookings flagged.
      releaseBookings?: boolean;
      allowPastRewrite?: boolean;
    }
  ) =>
    request<{
      unavailability: Unavailability;
      affectedBookings: AffectedBooking[];
    }>(`/resources/${resourceId}/unavailability`, { method: 'POST', body }),
  removeUnavailability: (resourceId: string, entryId: string) =>
    request<void>(`/resources/${resourceId}/unavailability/${entryId}`, { method: 'DELETE' }),

  // --- Jobs ---
  listJobs: (options: { status?: string; clientId?: string; search?: string; limit?: number } = {}) =>
    request<{ jobs: JobListItem[]; total: number }>(
      `/jobs${query({
        status: options.status,
        clientId: options.clientId,
        search: options.search,
        limit: options.limit?.toString(),
      })}`
    ),
  createJob: (body: {
    title: string;
    clientId?: string | null;
    siteAddress?: string | null;
    purchaseOrderNumber?: string | null;
    description?: string | null;
    startDate?: string | null;
    endDate?: string | null;
    jobManagerId?: string | null;
    clientRepId?: string | null;
  }) => request<{ job: Job }>('/jobs', { method: 'POST', body }),
  getJob: (id: string) =>
    request<{
      job: Job;
      clientName: string | null;
      jobManagerName: string | null;
      rep: ClientRep | null;
      notes: JobNote[];
      bookings: (Booking & { resourceName: string })[];
      /** The quote this job was made from (round 43): a link, never a value. */
      fromQuote: { id: string; number: string } | null;
      /** The purchase order as a file, beside the PO number (round 45). */
      purchaseOrderFile: Attachment | null;
    } & JobMoneyBlock>(`/jobs/${id}`),
  addJobNote: (id: string, body: string) =>
    request<{ note: JobNote }>(`/jobs/${id}/notes`, { method: 'POST', body: { body } }),
  updateJob: (id: string, body: Record<string, unknown>) =>
    request<{ job: Job }>(`/jobs/${id}`, { method: 'PATCH', body }),
  futureBookings: (id: string) =>
    request<{ bookings: (Booking & { resourceName: string })[] }>(`/jobs/${id}/future-bookings`),
  setJobStatus: (
    id: string,
    body: { status: JobStatus; cancelReason?: string | null; releaseFutureBookings?: boolean }
  ) =>
    request<{ job: Job; releasedBookings: unknown[] }>(`/jobs/${id}/status`, {
      method: 'POST',
      body,
    }),

  // --- Bookings and the calendar ---
  calendar: (params: {
    from: string;
    to: string;
    resourceId?: string;
    jobId?: string;
    clientId?: string;
    kind?: string;
    category?: string;
  }) => request<{ bookings: CalendarBooking[] }>(`/bookings${query(params)}`),
  /** Weekdays ride along on every call — one request answers the whole
      shading question, which is ZT's shape and the reason there is no
      separate settings fetch. */
  nonWorkingDays: (params?: { from?: string; to?: string }) =>
    request<{ weekdays: number[]; days: NonWorkingDay[] }>(
      `/non-working-days${query(params ?? {})}`
    ),
  saveNonWorkingWeekdays: (weekdays: number[]) =>
    request<{ weekdays: number[] }>('/non-working-days/weekdays', {
      method: 'PUT',
      body: { weekdays },
    }),
  createNonWorkingDay: (body: {
    name: string;
    startDate: string;
    endDate: string;
    kind: NonWorkingDayKind;
    notes?: string | null;
  }) => request<{ day: NonWorkingDay }>('/non-working-days', { method: 'POST', body }),
  updateNonWorkingDay: (
    id: string,
    body: {
      name: string;
      startDate: string;
      endDate: string;
      kind: NonWorkingDayKind;
      notes?: string | null;
    }
  ) => request<{ day: NonWorkingDay }>(`/non-working-days/${id}`, { method: 'PATCH', body }),
  deleteNonWorkingDay: (id: string) =>
    request<void>(`/non-working-days/${id}`, { method: 'DELETE' }),
  unavailabilityIn: (params: { from: string; to: string; resourceId?: string }) =>
    request<{ unavailability: Unavailability[] }>(`/bookings/unavailability${query(params)}`),
  /**
   * The flagged bookings, as WHOLE calendar bookings.
   *
   * Not a trimmed shape: the panel that shows these opens the booking's editor
   * from the row it is holding, and a partial row is what sent it hunting
   * through the window on screen — and moving the board when it missed.
   */
  needsReplanning: () =>
    request<{ bookings: CalendarBooking[] }>('/bookings/needs-replanning'),
  createBooking: (body: BookingInput & SaveOptions) =>
    request<{ booking: Booking }>('/bookings', { method: 'POST', body }),
  /** Maintenance takes no times — it is all-day by construction (013). */
  createMaintenance: (
    body: {
      resourceId: string;
      supplierId: string;
      startDate: string;
      endDate: string;
      note?: string | null;
    } & SaveOptions
  ) => request<{ booking: Booking }>('/bookings/maintenance', { method: 'POST', body }),
  /** `supplierId` is maintenance only — the server refuses it on a job booking. */
  updateBooking: (id: string, body: Partial<BookingInput> & { supplierId?: string } & SaveOptions) =>
    request<{ booking: Booking }>(`/bookings/${id}`, { method: 'PATCH', body }),
  /**
   * `allowPastRewrite` frees days that have already happened (round 16). Sent
   * as a query parameter rather than a body because this is a DELETE, and a
   * body on a DELETE is the kind of thing a proxy quietly drops.
   */
  releaseBooking: (id: string, allowPastRewrite = false) =>
    request<{ outcome: 'deleted' | 'truncated'; newEndDate?: string; rewrotePast: boolean }>(
      `/bookings/${id}${allowPastRewrite ? '?allowPastRewrite=true' : ''}`,
      { method: 'DELETE' }
    ),

  // --- A worker's own assignments ---
  mySchedule: (params: { from?: string; to?: string } = {}) =>
    request<{ resource: { id: string; name: string } | null; assignments: MyAssignment[] }>(
      `/my/schedule${query(params)}`
    ),
};
